import React, { ReactNode, useRef } from "react";
import { List, Modal } from "antd";
import Table from "../../lists/Table/Table";
import { useQuery } from "@apollo/client";
import {GET_ALL_WEBSITES, GET_ALL_MARKETPLACE_WEBSITES} from '../../../utils/api/website';
import {CampaignType, Website, WebsiteType} from '../../../__generated__/graphql';
import { ActionHandler } from '../../lists/Table/shared/ActionButton';
import { PencilIcon } from '@heroicons/react/24/outline';
import ActionButton from '../../lists/Table/shared/ActionButton';
import { NumberCell } from '../../lists/Table/shared/NumberCell';
import { useQueryVariables } from "../../../utils/hooks";
import { useCurrentUser } from "../../../utils/CurrentUserContext";

export const getFloorPriceProperty = (campaignType?: CampaignType) => campaignType=== CampaignType.Standard ? 'cprFloorPrice' : 'cpcFloorPrice'
export const getIsMarketplaceCampaign = (campaignType?: CampaignType) =>[CampaignType.Standard, CampaignType.MarketplaceNativeDisplay].includes(campaignType as CampaignType)

export const getWebsiteFloorPrice = (user, website: Website, campaignType?: CampaignType) => {
  const websiteFloorPrice = website[getFloorPriceProperty(campaignType)]
  return !website?.floorPriceExemptCompanyIds?.includes(user.companyId) ? websiteFloorPrice : null
}

export const confirmNewWebsites = (user, selectedWebsites: Website[], campaign, update, currentWebsiteIds: string[] = []) => {
    if (!getIsMarketplaceCampaign(campaign.type)) {
      return update()
    }

    const campaignBidPrice = campaign.rateCard?.price
    const websitesWithHigherFloorPrice = selectedWebsites.filter((website) => {
      const websiteFloorPrice = getWebsiteFloorPrice(user, website, campaign.type)

      if (!websiteFloorPrice || currentWebsiteIds.includes(website._id)) {
        return false
      }

      return websiteFloorPrice > campaignBidPrice
    })

    if (websitesWithHigherFloorPrice.length > 0) {
      return Modal.confirm({
        title: 'Are you sure?',
        content: (<>
        <p>You are about to add the following {websitesWithHigherFloorPrice.length} website(s)
        with a minimum bid higher than the bid price (<strong>{campaignBidPrice}</strong> DKK):</p>
        <List
          bordered
          className='mt-3 max-h-96 overflow-auto' 
          dataSource={websitesWithHigherFloorPrice}
          renderItem={item => (
            <List.Item>
              <List.Item.Meta
                title={item.name}
              />
              <p><strong>{item[getFloorPriceProperty(campaign.type)]}</strong> DKK</p>
            </List.Item>
          )}
        />
        </>),
        okText: 'Ok',
        onOk: update,
      })
    }

    return update()
}
const getColumns = (user, onEdit?: ActionHandler<Website>, campaignType?: CampaignType) => {
  const isMarketplaceCampaign = getIsMarketplaceCampaign(campaignType)

  return [
    {
      key: "website",
      title: "Website",
      dataIndex: "name",
    },
    {
      key: "type",
      title: "Type",
      dataIndex: "type",
      filters: Object.values(WebsiteType).map(status => {
        return {
          text: status.replaceAll('_', ' '), value: status
        }
      }),
      render: (value: string) => value.replaceAll('_', ' ')
    },
    {
      key: "minimumSpend",
      title: "Min. bid",
      width: 100,
      render: (_, website) => {
        const value = getWebsiteFloorPrice(user, website, campaignType)
        return value ? `${value} DKK` : ''
      },
      hidden: !isMarketplaceCampaign
    },
    {
      key: "visitors",
      title: "Visitors",
      dataIndex: "monthlyVisitors",
      render: NumberCell,
      width: isMarketplaceCampaign ? 90 : undefined,
    },
    ...onEdit
      ? [
        {
          key: "actions",
          title: "",
          render: (_: any, record: any) => <div className="px-3">
            <ActionButton tooltip="Edit Website" row={{original: record} as any} onClick={onEdit} Icon={PencilIcon} />
          </div>,
          meta: {
            noPadding: true,
          }
        }
      ]
      : []
  ]
};

interface WebsitesFormProps {
  className?: string;
  selectable?: boolean;
  filters?: {
    marketplace: string;
    campaignType: CampaignType
    contentSource?: string,
    websiteIds?: string[],
  }
  
  selectedWebsites?: string[];
  submitButton?: ReactNode,
  onChange?: (selectedWebsiteIds: string[], selectedWebsites: Website[]) => void;
  actionButtonText?: string;
  onActionButtonClick?: () => void;
  onEditWebsite?: (website: Website) => void;
  onRowClicked?: (website: Website) => void;
  getCheckboxProps?: (website: Website) => {disabled: boolean}
}

const WebsitesForm: React.FC<WebsitesFormProps> = ({
  className,
  selectable = true,
  selectedWebsites = [],
  onChange,
  filters,
  actionButtonText,
  onActionButtonClick,
  onEditWebsite,
  onRowClicked,
  submitButton,
  getCheckboxProps
}) => {
  const {user} = useCurrentUser()
  const { websiteIds: filterWebsiteIds, ...queryFilters } = filters || {} as any
  const initialFilters = filterWebsiteIds ? { ...queryFilters, _id: filterWebsiteIds } : queryFilters
  const {variables, setPagination, setFilters} = useQueryVariables(
    initialFilters,
    { column: 'createdAt', direction: 'DESC' },
    { current: 1, pageSize: 50 }
  )
  const { loading, error, data } = useQuery(filters?.marketplace ? GET_ALL_MARKETPLACE_WEBSITES : GET_ALL_WEBSITES, {
      variables
  });
  const websites = data?.websites.items || []
  const totalItems = data?.websites.meta.totalItems || 0
  const internalSelectedWebsites = useRef([[]])

  const handleRowSelectionChange = (rowKeys: any[], rowsSelected) => {
    const websitesFromOtherPages = selectedWebsites.filter((website) => !websites.find(({_id}: {_id: string}) => _id === website) )

    internalSelectedWebsites.current[variables.page as number] = rowsSelected
    const newRows = internalSelectedWebsites.current.flat()

    onChange?.([...websitesFromOtherPages, ...rowKeys], newRows);
  };

  return (
    <div className={className}>
      <Table
        columns={getColumns(user, onEditWebsite, filters?.campaignType)}
        data={websites || []}
        selectable={selectable}
        defaultRowSelection={selectedWebsites}
        onRowSelectionChange={handleRowSelectionChange}
        actionButtonText={actionButtonText}
        onActionButtonClick={onActionButtonClick}
        rowKey='_id'
        onRowClicked={onRowClicked}
        scroll={{y: '60vh'}}
        loading={loading}
        getCheckboxProps={getCheckboxProps}
        pagination={{
          total: totalItems,
          pageSize: variables.perPage,
          current: variables.page
        }}
        onChange={(newPagination, newFilters) => {
          setPagination(newPagination)
          setFilters({...variables.filters, ...newFilters})
        }}

      />
      {!loading && selectable && (
        <div className="my-5 py-3 flex justify-between">
          <span>
            {selectedWebsites.length} of {totalItems} Total
            Websites Selected
          </span>

          {submitButton}
        </div>
      )}
    </div>
  );
};

export default WebsitesForm;
